import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { createResultadoCorpoProva, deleteResultadoCorpoProva, getResultadoCorpoProva, updateResultadoCorpoProva, } from '../services/resultadoCorpoProvaService';
import { getCliente } from '../services/clienteService';
import { getObras } from '../services/obraService';
import { getCorpoProvaPorObra } from '../services/corpoProvaService';
import { getPrensa } from '../services/prensaService';
import { getTipoRupturaPorTipoCP } from '../services/tipoRupturaService';
import { getSessionClientId, getSessionRole } from '../services/authSession';
import ExpandableSearchFilter from '../components/ExpandableSearchFilter';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { normalizeDateOnly, formatDateBr } from '../utils/formatters';
import useSubmitLock from '../hooks/useSubmitLock';

const INITIAL_FORM = {
  idCliente: '',
  idObra: '',
  idCorpoProva: '',
  valrCarga: '',
  idTipoRuptura: '',
  observacao: '',
  valrA: '',
  idPrensa: '',
  dataRompimento: '',
};

// Formata um número no padrão pt-BR com até `fractionDigits` casas (ou '-' se inválido).
function formatDecimal(value, fractionDigits) {
  if (value === undefined || value === null || value === '') return '-';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '-';
  return parsed.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
}

// Converte entrada decimal pt-BR ("1,5") em número (1.5).
function parseDecimalInput(value) {
  return Number(String(value).replace(',', '.'));
}

// Normaliza um corpo de prova da API para o formato da tela.
function normalizeCorpoProva(item) {
  const idObra = String(
    item.id_obra || item.idObra || item.id_obra_concretagem || item.idObraConcretagem || '');

  // AJUSTE BACKEND AQUI: mantenha estas chaves sincronizadas com o retorno da API de corpo de prova.
  return {
    id: String(item.id || item.id_corpo_prova || item.idCorpoProva || ''),
    etiqueta: item.etiqueta || item.codigo || item.codg_corpo_prova || item.nome || item.idenObraEtiqueta || '-',
    dataRuptura: normalizeDateOnly(item.data_ruptura || item.dataRuptura || item.data_rompimento || item.dataRuptura || ''),
    idCliente: String(item.id_cliente || item.idCliente || ''),
    idObra,
    nomeObra: item.nome_obra || item.nomeObra || item.obra || (idObra ? `Obra ${idObra}` : '-'),
    idTipoCorpoProva: String(item.tipoCorpoProva?.idTipoCorpoProva || item.idTipoCorpoProva || ''),
  };
}

// Normaliza uma prensa da API para { id, nome } usado no select.
function normalizePrensa(item) {
  return {
    id: String(item.id || item.id_prensa || item.idPrensa || ''),
    nome: item.codgIdentificacao || item.nome || item.nome_prensa || item.descricao || `Prensa ${item.id || item.id_prensa || ''}`,
  };
}

/*function normalizeCliente(item) {
  return {
    id: String(item.id || item.id_cliente || item.idCliente || ''),
    nome: item.razao_social || item.razaoSocial || item.nome || item.nome_fantasia || '-',
  };
}*/

// Normaliza um tipo de ruptura da API para { id, nome } usado no select.
function normalizeTipoRuptura(item) {
  return {
    id: String(item.tipoRuptura?.idTipoRuptura || item.id || item.id_tipo_ruptura || item.idTipoRuptura || ''),
    nome: item.tipoRuptura?.nomeTipoRuptura || item.nomeTipoRuptura || item.nome_tipo_ruptura || item.nome || item.sigla || item.codigo || '-',
  };
}

// Normaliza um resultado de corpo de prova da API (com dados aninhados de CP/prensa/ruptura) para a tela.
function normalizeResultado(item) {
  // AJUSTE BACKEND AQUI: chaves aceitas para resposta de resultado_corpo_prova.
  return {
    id: String(item.id || item.id_resu_corpo_prova || item.id_resultado_corpo_prova || item.idResultadoCorpoProva || item.idResuCorpoProva || ''),
    idCliente: String(item.id_cliente || item.idCliente || ''),
    idCorpoProva: String(item.corpoProva?.idCorpoProva || item.id_corpo_prova || item.idCorpoProva || ''),
    valrCarga: item.valrCarga ?? item.valr_carga ?? item.carga ?? '',
    idTipoRuptura: String(item.tipoRuptura?.idTipoRuptura || item.id_tipo_ruptura || item.idTipoRuptura || ''),
    observacao: item.observacao || item.observacoes || item.obs || '',
    valrA: item.valrA ?? item.valr_a ?? item.valor_a ?? item.valorA ?? '',
    idPrensa: String(item.prensa?.idPrensa || item.id_prensa || item.idPrensa || ''),
    dataRompimento: normalizeDateOnly(item.dataRompimento || item.data_rompimento || ''),
    corpoProvaEtiqueta: item.corpoProva?.idenObraEtiqueta || '',
    corpoProvaDataRuptura: normalizeDateOnly(item.corpoProva?.dataRuptura || ''),
    idObra: String(item.corpoProva?.idObra || ''),
    nomeObra: item.corpoProva?.nomeObra || '',
    tipoRupturaNome: item.tipoRuptura?.nomeTipoRuptura || '',
    tipoCargaNome: item.tipoCarga?.nomeTipoCarga || '',
    prensaNome: item.prensa?.codgIdentificacao || '',
  };
}

// Retorna a data de hoje no formato YYYY-MM-DD.
function getTodayDateKey() {
  const now = new Date();
  const pad = (num) => String(num).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// Permite apenas dígitos, vírgula/ponto e teclas de navegação nos campos decimais.
function isAllowedDecimalKey(event) {
  if (event.ctrlKey || event.metaKey || event.altKey) return true;
  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
  return allowedKeys.includes(event.key) || /[\d,.]/.test(event.key);
}

// Tela de Resultados de Corpos de Prova: registra carga, tipo de ruptura e prensa para cada CP de uma obra.
function ResultadosCorposProvaPage() {
  const role = getSessionRole();
  const sessionClientId = String(getSessionClientId() || '');
  const canManage = role === 'admin';
  const canCreate = role === 'admin' || role === 'consultor' || !role;

  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({ ...INITIAL_FORM, idCliente: sessionClientId });

  const [resultados, setResultados] = useState([]);
  const [corposProva, setCorposProva] = useState([]);
  const [prensas, setPrensas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tiposRuptura, setTiposRuptura] = useState([]);
  const [obras, setObras] = useState([]);

  const [corposProvaCadastro, setCorposProvaCadastro] = useState([]);
  const [tiposRupturaCadastro, setTiposRupturaCadastro] = useState([]);

  // Carrega em paralelo resultados, obras, prensas e clientes (listagem e selects).
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      // AJUSTE BACKEND AQUI: esta pagina depende destes endpoints para montar os FKs da tela.
      const [resultadosResult, obrasResult, prensasResult, clientesResult] = await Promise.allSettled([
        getResultadoCorpoProva(),
        getObras(),
        getPrensa(),
        getCliente(),
      ]);

      if (!isMounted) return;

      if (resultadosResult.status === 'fulfilled' && Array.isArray(resultadosResult.value)) {
        setResultados(resultadosResult.value.map(normalizeResultado).filter((item) => item.id));
      }

      if (obrasResult.status === 'fulfilled' && Array.isArray(obrasResult.value)) {
        setObras(obrasResult.value
          .map((item) => ({
            id: String(item.idObra || ''),
            nome: item.nomeObra || `Obra ${item.idObra}`,
            idCliente: String(item.idCliente || ''),
          }))
          .filter((item) => item.id));
      }

      if (prensasResult.status === 'fulfilled' && Array.isArray(prensasResult.value)) {
        setPrensas(prensasResult.value.map(normalizePrensa).filter((item) => item.id));
      }

      /*if (clientesResult.status === 'fulfilled' && Array.isArray(clientesResult.value)) {
        setClientes(clientesResult.value.map(normalizeCliente).filter((item) => item.id));
      }*/

      setLoading(false);
    };

    loadData().catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  // Carrega corpos de prova dinamicamente quando a Obra selecionada muda
  useEffect(() => {
    if (!formData.idObra) {
      setCorposProvaCadastro([]);
      return;
    }

    let isMounted = true;
    const loadCorpos = async () => {
      try {
        const data = await getCorpoProvaPorObra(formData.idObra);
        if (!isMounted) return;
        if (Array.isArray(data)) {
          const selectedObra = obras.find((o) => String(o.id) === String(formData.idObra));
          const nomeObra = selectedObra?.nome || '';
          setCorposProvaCadastro(
            data.map((item) => normalizeCorpoProva({ ...item, idObra: formData.idObra, nomeObra })).filter((item) => item.id)
          );
        } else {
          setCorposProvaCadastro([]);
        }
      } catch (error) {
        if (isMounted) setCorposProvaCadastro([]);
      }
    };

    loadCorpos();
    return () => {
      isMounted = false;
    };
  }, [formData.idObra, obras]);

  // Carrega os tipos de ruptura dinamicamente quando o Corpo de Prova selecionado muda
  useEffect(() => {
    if (!formData.idCorpoProva) {
      setTiposRupturaCadastro([]);
      return;
    }

    const selectedCorpo = corposProvaCadastro.find((item) => String(item.id) === String(formData.idCorpoProva));
    if (!selectedCorpo || !selectedCorpo.idTipoCorpoProva) {
      setTiposRupturaCadastro([]);
      return;
    }

    let isMounted = true;
    const loadTiposRuptura = async () => {
      try {
        const data = await getTipoRupturaPorTipoCP(selectedCorpo.idTipoCorpoProva);
        if (!isMounted) return;
        if (Array.isArray(data)) {
          setTiposRupturaCadastro(data.map(normalizeTipoRuptura).filter((item) => item.id));
        } else {
          setTiposRupturaCadastro([]);
        }
      } catch (error) {
        if (isMounted) setTiposRupturaCadastro([]);
      }
    };

    loadTiposRuptura();
    return () => {
      isMounted = false;
    };
  }, [formData.idCorpoProva, corposProvaCadastro]);

  const corpoProvaMap = useMemo(() => {
    return corposProva.reduce((acc, item) => {
      acc[String(item.id)] = item;
      return acc;
    }, {});
  }, [corposProva]);

  const prensaMap = useMemo(() => {
    return prensas.reduce((acc, item) => {
      acc[String(item.id)] = item.nome;
      return acc;
    }, {});
  }, [prensas]);

  const clienteMap = useMemo(() => {
    return clientes.reduce((acc, item) => {
      acc[String(item.id)] = item.nome;
      return acc;
    }, {});
  }, [clientes]);

  const tipoRupturaMap = useMemo(() => {
    return tiposRuptura.reduce((acc, item) => {
      acc[String(item.id)] = item.nome;
      return acc;
    }, {});
  }, [tiposRuptura]);

  const resultadosDoCliente = useMemo(() => {
    return resultados;
  }, [resultados]);

  const corposProvaDoCliente = useMemo(() => {
    return corposProva;
  }, [corposProva]);

  const obraOptions = useMemo(() => {
    return [...obras].sort((a, b) => String(a.nome).localeCompare(String(b.nome)));
  }, [obras]);

  const filteredCorposProva = useMemo(() => {
    return corposProvaCadastro
      .filter((item) => (formData.idObra ? String(item.idObra) === String(formData.idObra) : true))
      .filter((item) => (formData.dataRompimento ? item.dataRuptura === formData.dataRompimento : true))
      .sort((a, b) => String(a.etiqueta).localeCompare(String(b.etiqueta)));
  }, [corposProvaCadastro, formData.dataRompimento, formData.idObra]);

  const filteredResultados = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return resultadosDoCliente;

    return resultadosDoCliente.filter((item) => {
      const corpo = corpoProvaMap[String(item.idCorpoProva)]?.etiqueta || item.corpoProvaEtiqueta || '';
      const obra = corpoProvaMap[String(item.idCorpoProva)]?.nomeObra || item.nomeObra || '';
      const prensaNome = prensaMap[String(item.idPrensa)] || item.prensaNome || '';
      const tipo = tipoRupturaMap[String(item.idTipoRuptura)] || item.tipoRupturaNome || '';
      const data = formatDateBr(item.dataRompimento);
      const cliente = clienteMap[String(item.idCliente)] || '';

      return (
        corpo.toLowerCase().includes(normalized) ||
        obra.toLowerCase().includes(normalized) ||
        prensaNome.toLowerCase().includes(normalized) ||
        tipo.toLowerCase().includes(normalized) ||
        cliente.toLowerCase().includes(normalized) ||
        data.toLowerCase().includes(normalized)
      );
    });
  }, [clienteMap, corpoProvaMap, prensaMap, query, resultadosDoCliente, tipoRupturaMap]);

  const stats = useMemo(() => {
    const hoje = getTodayDateKey();
    const total = resultadosDoCliente.length;
    const romperHoje = resultadosDoCliente.filter((item) => normalizeDateOnly(item.dataRompimento) === hoje).length;
    const comObservacao = resultadosDoCliente.filter((item) => String(item.observacao || '').trim()).length;
    return { total, romperHoje, comObservacao };
  }, [resultadosDoCliente]);

  // Handler de onChange por campo; ao mudar obra/data, reseta CP e tipo de ruptura dependentes.
  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((prev) => {
      if (field === 'idObra' || field === 'dataRompimento') {
        return { ...prev, [field]: value, idCorpoProva: '', idTipoRuptura: '' };
      }
      return { ...prev, [field]: value };
    });

    setFormErrors((prev) => {
      if (!prev[field] && !(field === 'idObra' && (prev.idCorpoProva || prev.idTipoRuptura)) && !(field === 'dataRompimento' && (prev.idCorpoProva || prev.idTipoRuptura))) {
        return prev;
      }

      const next = { ...prev };
      delete next[field];

      if (field === 'idObra' || field === 'dataRompimento') {
        delete next.idCorpoProva;
        delete next.idTipoRuptura;
      }

      return next;
    });
  };

  // Ao selecionar um CP, preenche obra/data a partir dele e limpa o tipo de ruptura.
  const handleCorpoProvaChange = (event) => {
    const selectedId = event.target.value;
    const selectedCorpo = corposProvaCadastro.find((item) => String(item.id) === String(selectedId));

    setFormData((prev) => ({
      ...prev,
      idCorpoProva: selectedId,
      idObra: selectedCorpo?.idObra || prev.idObra,
      dataRompimento: selectedCorpo?.dataRuptura || prev.dataRompimento,
      idTipoRuptura: '',
    }));

    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.idCorpoProva;
      delete next.dataRompimento;
      delete next.idTipoRuptura;
      return next;
    });
  };

  // Abre o modal em modo de criação (reseta o formulário).
  const openModal = () => {
    setEditingId(null);
    setFormData({ ...INITIAL_FORM, idCliente: sessionClientId });
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Abre o modal em modo de edição, preenchendo o formulário com os dados do resultado.
  const openEditModal = (item) => {
    setEditingId(item.id);
    setFormData({
      idCliente: sessionClientId || String(item.idCliente || ''),
      idObra: String(item.idObra || ''),
      idCorpoProva: String(item.idCorpoProva || ''),
      valrCarga: String(item.valrCarga ?? ''),
      idTipoRuptura: String(item.idTipoRuptura || ''),
      observacao: String(item.observacao || ''),
      valrA: String(item.valrA ?? ''),
      idPrensa: String(item.idPrensa || ''),
      dataRompimento: normalizeDateOnly(item.dataRompimento || ''),
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Fecha o modal e limpa edição/erros.
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormErrors({});
  };

  // Valida obrigatórios e limites/casas decimais (carga, valor a) e checa resultado duplicado (CP + data).
  const validateForm = () => {
    const nextErrors = {};

    if (!formData.idCorpoProva) {
      nextErrors.idCorpoProva = 'Corpo de prova obrigatório.';
    }

    if (!formData.valrCarga) {
      nextErrors.valrCarga = 'Carga obrigatória.';
    } else {
      const parsed = parseDecimalInput(formData.valrCarga);
      if (!Number.isFinite(parsed)) {
        nextErrors.valrCarga = 'Carga invalida.';
      } else if (parsed < 0 || parsed > 9999.999) {
        nextErrors.valrCarga = 'Carga deve ficar entre 0 e 9999,999.';
      } else {
        const decimalPart = String(formData.valrCarga).split(/[.,]/)[1] || '';
        if (decimalPart.length > 3) nextErrors.valrCarga = 'Carga aceita ate 3 casas decimais.';
      }
    }

    if (!formData.idTipoRuptura) {
      nextErrors.idTipoRuptura = 'Tipo de ruptura obrigatório.';
    }

    if (formData.valrA) {
      const parsed = parseDecimalInput(formData.valrA);
      if (!Number.isFinite(parsed)) {
        nextErrors.valrA = 'Valor a (mm) inválido.';
      } else if (parsed < 0 || parsed > 9999.99) {
        nextErrors.valrA = 'Valor a (mm) deve ficar entre 0 e 9999,99.';
      } else {
        const decimalPart = String(formData.valrA).split(/[.,]/)[1] || '';
        if (decimalPart.length > 2) nextErrors.valrA = 'Valor a (mm) aceita ate 2 casas decimais.';
      }
    }

    if (!formData.idPrensa) {
      nextErrors.idPrensa = 'Prensa utilizada obrigatória.';
    }

    if (!formData.dataRompimento) {
      nextErrors.dataRompimento = 'Data do rompimento obrigatória.';
    }

    const duplicate = resultadosDoCliente.some((item) => {
      if (editingId && item.id === editingId) return false;

      return (
        String(item.idCorpoProva || '') === String(formData.idCorpoProva || '') &&
        normalizeDateOnly(item.dataRompimento) === normalizeDateOnly(formData.dataRompimento)
      );
    });

    if (duplicate) {
      nextErrors.idCorpoProva = 'Ja existe resultado para este corpo de prova nesta data.';
    }

    return nextErrors;
  };

  // Submete o form: valida, cria/atualiza na API e enriquece o item local com nomes de CP/obra/ruptura/prensa.
  const [submitting, handleSave] = useSubmitLock(async (event) => {
    event.preventDefault();

    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    const payloadFormData = {
      ...formData,
      idCliente: sessionClientId || formData.idCliente,
    };

    /*if (!payloadFormData.idCliente) {
      setFormErrors({ geral: 'Cliente da sessão não encontrado.' });
      return;
    }*/

    try {
      const selectedCorpo = corposProvaCadastro.find((c) => String(c.id) === String(payloadFormData.idCorpoProva));
      const selectedObra = obras.find((o) => String(o.id) === String(payloadFormData.idObra));
      const selectedRuptura = tiposRupturaCadastro.find((t) => String(t.id) === String(payloadFormData.idTipoRuptura));
      const selectedPrensa = prensas.find((p) => String(p.id) === String(payloadFormData.idPrensa));

      if (editingId) {
        const { response, payload } = await updateResultadoCorpoProva(editingId, payloadFormData);
        const oldItem = resultados.find((r) => String(r.id) === String(editingId));

        const baseObj = response || { id: editingId, ...payloadFormData, ...payload };
        const enrichedObj = {
          ...baseObj,
          corpoProva: baseObj.corpoProva || {
            idCorpoProva: payloadFormData.idCorpoProva,
            idenObraEtiqueta: selectedCorpo?.etiqueta || oldItem?.corpoProvaEtiqueta || '',
            dataRuptura: selectedCorpo?.dataRuptura || oldItem?.corpoProvaDataRuptura || '',
            idObra: payloadFormData.idObra,
            nomeObra: selectedObra?.nome || oldItem?.nomeObra || '',
          },
          tipoRuptura: baseObj.tipoRuptura || {
            idTipoRuptura: payloadFormData.idTipoRuptura,
            nomeTipoRuptura: selectedRuptura?.nome || oldItem?.tipoRupturaNome || '',
          },
          prensa: baseObj.prensa || {
            idPrensa: payloadFormData.idPrensa,
            codgIdentificacao: selectedPrensa?.nome || oldItem?.prensaNome || '',
          },
        };

        const normalized = normalizeResultado(enrichedObj);

        setResultados((prev) => prev.map((item) => (item.id === editingId ? normalized : item)));
        showSuccessToast('Resultado atualizado com sucesso.');
      } else {
        const { response, payload } = await createResultadoCorpoProva(payloadFormData);

        const baseObj = response || { id: `res-${Date.now()}`, ...payloadFormData, ...payload };
        const enrichedObj = {
          ...baseObj,
          corpoProva: baseObj.corpoProva || {
            idCorpoProva: payloadFormData.idCorpoProva,
            idenObraEtiqueta: selectedCorpo?.etiqueta || '',
            dataRuptura: selectedCorpo?.dataRuptura || '',
            idObra: payloadFormData.idObra,
            nomeObra: selectedObra?.nome || '',
          },
          tipoRuptura: baseObj.tipoRuptura || {
            idTipoRuptura: payloadFormData.idTipoRuptura,
            nomeTipoRuptura: selectedRuptura?.nome || '',
          },
          prensa: baseObj.prensa || {
            idPrensa: payloadFormData.idPrensa,
            codgIdentificacao: selectedPrensa?.nome || '',
          },
        };

        const normalized = normalizeResultado(enrichedObj);

        setResultados((prev) => [normalized, ...prev]);
        showSuccessToast('Resultado cadastrado com sucesso.');
      }

      closeModal();
    } catch (error) {
      setFormErrors({
        geral: error?.message || 'Não foi possível salvar o resultado.',
      });
    }
  });

  // Exclui o resultado após confirmação (restrito a admin) e remove da lista local.
  const handleDelete = async (id) => {
    if (!canManage) return;

    const confirmed = window.confirm('Deseja realmente excluir este resultado?');
    if (!confirmed) return;

    try {
      await deleteResultadoCorpoProva(id);
      setResultados((prev) => prev.filter((item) => item.id !== id));
      showSuccessToast('Resultado excluído com sucesso.');
    } catch (error) {
      showErrorToast(error?.message || 'Não foi possível excluir o resultado.');
    }
  };

  return (
    <div className="resultados-page space-y-6">
      <section className="resultados-header flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Resultados de corpos de prova</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Visualize e gerencie os resultados de rompimento por corpo de prova com obra, prensa e tipo de ruptura.
          </p>
        </div>
        <button
          type="button"
          onClick={openModal}
          disabled={!canCreate}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          Novo resultado
        </button>
      </section>

      <section className="resultados-metrics grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Romper hoje</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.romperHoje}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Com observações</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.comObservacao}</p>
        </div>
      </section>

      <section className="resultados-table overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
          <ExpandableSearchFilter
            variant="block"
            query={query}
            onChange={setQuery}
            placeholder="Buscar por corpo de prova, obra, prensa ou data"
            resultCount={filteredResultados.length}
          />
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="px-4 py-3">Corpo de prova</th>
                <th className="px-4 py-3">Obra</th>
                <th className="px-4 py-3">Carga</th>
                <th className="px-4 py-3">Tipo ruptura</th>
                <th className="px-4 py-3">Valor a (mm)</th>
                <th className="px-4 py-3">Prensa</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">
                  <div className={`flex items-center ${canManage ? 'justify-between gap-3' : 'justify-end'}`}>
                    <span>Ações</span>
                    {canManage ? (
                      <ExpandableSearchFilter
                        variant="inline"
                        query={query}
                        onChange={setQuery}
                        placeholder="Buscar..."
                        resultCount={filteredResultados.length}
                      />
                    ) : null}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`sk-${index}`} className="border-b border-slate-100">
                    {Array.from({ length: 8 }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredResultados.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-500">
                    Nenhum resultado encontrado.
                  </td>
                </tr>
              ) : (
                filteredResultados.map((item) => {
                  const corpoProva = corpoProvaMap[String(item.idCorpoProva)];

                  return (
                    <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80">
                      <td className="px-4 py-4 font-medium text-slate-900">{corpoProva?.etiqueta || item.corpoProvaEtiqueta || '-'}</td>
                      <td className="px-4 py-4 text-slate-600">{corpoProva?.nomeObra || item.nomeObra || '-'}</td>
                      <td className="px-4 py-4 text-slate-600">{formatDecimal(item.valrCarga, 3)}</td>
                      <td className="px-4 py-4 text-slate-600">{tipoRupturaMap[String(item.idTipoRuptura)] || item.tipoRupturaNome || '-'}</td>
                      <td className="px-4 py-4 text-slate-600">{formatDecimal(item.valrA, 2)}</td>
                      <td className="px-4 py-4 text-slate-600">{prensaMap[String(item.idPrensa)] || item.prensaNome || '-'}</td>
                      <td className="px-4 py-4 text-slate-600">{formatDateBr(item.dataRompimento)}</td>
                      <td className="px-4 py-4">
                        {canManage ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(item)}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Excluir
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Somente leitura</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`mobile-sk-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredResultados.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Nenhum resultado encontrado.
            </div>
          ) : (
            filteredResultados.map((item) => {
              const corpoProva = corpoProvaMap[String(item.idCorpoProva)];

              return (
                <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-base font-semibold text-slate-900">{corpoProva?.etiqueta || item.corpoProvaEtiqueta || '-'}</p>
                  <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-slate-600">
                    <span>Obra: {corpoProva?.nomeObra || item.nomeObra || '-'}</span>
                    <span>Carga: {formatDecimal(item.valrCarga, 3)}</span>
                    <span>Tipo de ruptura: {tipoRupturaMap[String(item.idTipoRuptura)] || item.tipoRupturaNome || '-'}</span>
                    <span>Prensa: {prensaMap[String(item.idPrensa)] || item.prensaNome || '-'}</span>
                    <span>Data: {formatDateBr(item.dataRompimento)}</span>
                  </div>
                  {canManage ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openEditModal(item)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Excluir
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </section>
      {isModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeModal}
            aria-label="Fechar modal"
          />
          <form
            onSubmit={handleSave}
            className="relative z-10 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:p-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Cadastro</p>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingId ? 'Editar resultado' : 'Cadastrar resultado'}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Preencha os dados do rompimento para manter o historico dos corpos de prova atualizado.
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {formErrors.geral ? (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formErrors.geral}
              </div>
            ) : null}

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="resultado-obra" className="text-sm font-medium text-slate-700">
                  Obra:
                </label>
                <select
                  id="resultado-obra"
                  value={formData.idObra}
                  onChange={handleFieldChange('idObra')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="">Todas</option>
                  {obraOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label htmlFor="resultado-data" className="text-sm font-medium text-slate-700">
                  Data de rompimento <span className="text-rose-500">*</span>
                </label>
                <input
                  id="resultado-data"
                  type="date"
                  value={formData.dataRompimento}
                  onChange={handleFieldChange('dataRompimento')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                {formErrors.dataRompimento ? <p className="text-xs text-rose-600">{formErrors.dataRompimento}</p> : null}
              </div>

              <div className="space-y-2 sm:col-span-2">
                <label htmlFor="resultado-corpo-prova" className="text-sm font-medium text-slate-700">
                  Corpo de prova <span className="text-rose-500">*</span>
                </label>
                <select
                  id="resultado-corpo-prova"
                  value={formData.idCorpoProva}
                  onChange={handleCorpoProvaChange}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="">{filteredCorposProva.length === 0 ? 'Nenhum corpo de prova para os filtros' : 'Selecione'}</option>
                  {filteredCorposProva.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.etiqueta} | {item.nomeObra} | {formatDateBr(item.dataRuptura)}
                    </option>
                  ))}
                </select>
                {formErrors.idCorpoProva ? <p className="text-xs text-rose-600">{formErrors.idCorpoProva}</p> : null}
              </div>

              <div className="space-y-2">
                <label htmlFor="resultado-carga" className="text-sm font-medium text-slate-700">
                  Carga <span className="text-rose-500">*</span>
                </label>
                <input
                  id="resultado-carga"
                  type="text"
                  value={formData.valrCarga}
                  onChange={handleFieldChange('valrCarga')}
                  onKeyDown={(event) => {
                    if (!isAllowedDecimalKey(event)) event.preventDefault();
                  }}
                  placeholder="Ex: 1234,567"
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                <p className="text-[11px] text-slate-500">Ate 4 casas antes da virgula e ate 3 depois.</p>
                {formErrors.valrCarga ? <p className="text-xs text-rose-600">{formErrors.valrCarga}</p> : null}
              </div>

              <div className="space-y-2">
                <label htmlFor="resultado-tipo-ruptura" className="text-sm font-medium text-slate-700">
                  Tipo de ruptura <span className="text-rose-500">*</span>
                </label>
                <select
                  id="resultado-tipo-ruptura"
                  value={formData.idTipoRuptura}
                  onChange={handleFieldChange('idTipoRuptura')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="">Selecione</option>
                  {tiposRupturaCadastro.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </option>
                  ))}
                </select>
                {formErrors.idTipoRuptura ? <p className="text-xs text-rose-600">{formErrors.idTipoRuptura}</p> : null}
              </div>

              <div className="space-y-2">
                <label htmlFor="resultado-valor-a" className="text-sm font-medium text-slate-700">
                  Valor a (mm):
                </label>
                <input
                  id="resultado-valor-a"
                  type="text"
                  value={formData.valrA}
                  onChange={handleFieldChange('valrA')}
                  onKeyDown={(event) => {
                    if (!isAllowedDecimalKey(event)) event.preventDefault();
                  }}
                  placeholder="Ex: 1234,56"
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                <p className="text-[11px] text-slate-500">Ate 4 casas antes da virgula e ate 2 depois.</p>
                {formErrors.valrA ? <p className="text-xs text-rose-600">{formErrors.valrA}</p> : null}
              </div>

              <div className="space-y-2">
                <label htmlFor="resultado-prensa" className="text-sm font-medium text-slate-700">
                  Prensa utilizada <span className="text-rose-500">*</span>
                </label>
                <select
                  id="resultado-prensa"
                  value={formData.idPrensa}
                  onChange={handleFieldChange('idPrensa')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="">Selecione</option>
                  {prensas.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.nome}
                    </option>
                  ))}
                </select>
                {formErrors.idPrensa ? <p className="text-xs text-rose-600">{formErrors.idPrensa}</p> : null}
              </div>

              <div className="space-y-2 sm:col-span-2">
                <label htmlFor="resultado-observacao" className="text-sm font-medium text-slate-700">
                  Observações:
                </label>
                <textarea
                  id="resultado-observacao"
                  rows={3}
                  value={formData.observacao}
                  onChange={handleFieldChange('observacao')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? 'Salvando...' : editingId ? 'Salvar alterações' : 'Salvar resultado'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default ResultadosCorposProvaPage;

